import React, { useState } from "react";
import {
  IconButton,
  InputAdornment,
  TextFieldProps,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useTranslate } from "src/locales";
import ByzMuiTextField from "./ByzMuiTextField";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";

export interface PopupOption {
  value: string;
  label: string;
}

export type ByzMuiPopupInputProps = Omit<TextFieldProps, 'onChange'> & {
  label?: string;
  dialogTitle?: string;
  options: PopupOption[];
  value?: string;
  onChange?: (value: string) => void;
  displayField?: string; // Hangi field gösterilecek (varsayılan: label)
  showPatientData?: boolean; // Patient datas gösterilsin mi (varsayılan: false)
};

const ByzMuiPopupInput: React.FC<ByzMuiPopupInputProps> = ({
  label,
  dialogTitle,
  options = [],
  value = "",
  onChange,
  displayField = "label",
  showPatientData = false,
  ...rest
}) => {
  const { t } = useTranslate();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    if (!rest.disabled) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelect = (selectedValue: string) => {
    // Eğer seçilen değer mevcut value ile aynıysa, boş string gönder (seçimi kaldır)
    const newValue = value === selectedValue ? "" : selectedValue;
    onChange?.(newValue);
    handleClose();
  };

  // Seçili değerin label'ını bul
  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption
    ? selectedOption[displayField as keyof PopupOption] || selectedOption.label
    : "";
  
  // Placeholder için boş değer kontrolü
  const showPlaceholder = !value || value === "";

  return (
    <>
      <ByzMuiTextField
        {...rest}
        label={label}
        value={showPlaceholder ? "" : displayValue}
        placeholder={showPlaceholder ? (rest.placeholder || t("buttons.chose")) : undefined}
        inputProps={{
          readOnly: true,
          style: { cursor: rest.disabled ? "not-allowed" : "pointer" },
        }}
        onClick={handleOpen}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpen();
                }}
                disabled={rest.disabled}
              >
                <MoreHorizIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          width: rest.fullWidth ? "100%" : "180px",
          ...rest.sx,
        }}
      />

      <OpenComponentDialog
        title={dialogTitle || label || t("buttons.chose")}
        open={open}
        showOkButton={false}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth={false}
        showPatientData={showPatientData}
      >
        <List>
          {options.map((option) => (
            <ListItem key={option.value} disablePadding>
              <ListItemButton
                selected={value === option.value}
                onClick={() => handleSelect(option.value)}
              >
                <ListItemText primary={option.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </OpenComponentDialog>
    </>
  );
};

export default ByzMuiPopupInput;

